/**
 * Known `error` values from failed auth REST responses (`{ error: string }`).
 * The client throws that string as `Error.message` (@dabble/rest-client).
 * Add a matching key in `src/i18n/locales/*.json` wherever we show the message with `te(message)`.
 *
 * Confirmed for signup: `EMAIL_EXISTS` (see dabble-server hosting signup flow).
 * Others are used across login / account flows and legacy Dabble accounts UI.
 */
export const AUTH_API_ERROR_CODES = [
  'EMAIL_EXISTS',
  'EMAIL_NOT_FOUND',
  'INVALID_ARGUMENTS',
  'INVALID_CREDENTIALS',
  'INVALID_EMAIL',
  // Firebase's email-enumeration-protection code for a bad email/password — same
  // meaning as INVALID_CREDENTIALS; both map to the same "incorrect" copy.
  'INVALID_LOGIN_CREDENTIALS',
  'INVALID_OLD_PASSWORD',
  'INVALID_PASSWORD',
  'SOMETHING_HAPPENED',
  'TOO_MANY_ATTEMPTS_TRY_LATER',
  'USER_NOT_FOUND',
  'WEAK_PASSWORD',
] as const;

export type AuthApiErrorCode = (typeof AUTH_API_ERROR_CODES)[number];

/**
 * Known `error` values from `POST /auth/google` (SSO sign-in):
 * - `AUTH_FAILED` — invalid / expired Google ID token (401).
 * - `ACCOUNT_EXISTS` — the email belongs to an existing account that needs
 *   confirmation before linking (409).
 * - `NO_LINKED_ACCOUNT` — the login surface (`mode:'signin'`) got a Google
 *   address that matches no account (404). The backend refuses to fork a new
 *   account here; we point the user at password login + profile-connect, or sign-up.
 * - `EMAIL_NOT_VERIFIED` — the Google address matches an existing account whose
 *   email was never verified (403). The backend won't link into it (anti-
 *   hijack) and has already sent a fresh verification email; we tell the user
 *   to verify first, then retry.
 *
 * Unlike the password codes above, these are NOT their own i18n keys — they map
 * to the `error_sso_*` keys via `ssoErrorMessageKey` below.
 */
export const SSO_API_ERROR_CODES = [
  'AUTH_FAILED',
  'ACCOUNT_EXISTS',
  'NO_LINKED_ACCOUNT',
  'EMAIL_NOT_VERIFIED',
] as const;

export type SsoApiErrorCode = (typeof SSO_API_ERROR_CODES)[number];

/**
 * Emitted by `AuthSsoButtons` (not the API) when Google Identity Services
 * itself fails to load or produce a credential (script blocked, no network…).
 */
export const SSO_LOAD_ERROR_CODE = 'SSO_FAILED';

/**
 * Map an SSO error code — API (`SSO_API_ERROR_CODES`) or client-side
 * (`SSO_LOAD_ERROR_CODE`) — to the i18n key the auth views show in their Alert.
 * Anything unrecognized gets the generic failure copy.
 */
export function ssoErrorMessageKey(code: string): string {
  if (code === 'ACCOUNT_EXISTS') return 'error_sso_account_exists';
  if (code === 'NO_LINKED_ACCOUNT') return 'error_sso_no_account';
  if (code === 'EMAIL_NOT_VERIFIED') return 'error_sso_email_not_verified';
  return 'error_sso_failed';
}
